import { useState, useCallback } from 'react'
import {
  X,
  Crown,
  AlertCircle,
  Check,
  Pencil,
  DoorOpen,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { useFactionWithMembers } from '@/hooks/useFactionWithMembers'
import { useParty } from '@/hooks/useParty'
import { useUpdateFactionName } from '@/hooks/useUpdateFactionName'
import { FactionStatusBadge } from './FactionStatusBadge'
import { FactionSatisfactionBar } from './FactionSatisfactionBar'
import { FactionIdeologyComparison } from './FactionIdeologyComparison'
import { FactionDemandAlert } from './FactionDemandAlert'
import { CandidateStatBar } from '@/components/candidate/CandidateStatBar'
import { CandidateRoleBadge } from '@/components/candidate/CandidateRoleBadge'
import type { FactionMembershipWithCandidate, FactionDemandDetails } from '@/types'

export interface FactionDetailProps {
  factionId: string
  onClose?: () => void
  editable?: boolean
  currentTick?: number
  className?: string
}

interface MemberRowProps {
  member: FactionMembershipWithCandidate
  isLeader: boolean
}

function MemberRow({ member, isLeader }: MemberRowProps) {
  const { candidate } = member

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-md',
        isLeader && 'bg-amber-500/5 border border-amber-500/20'
      )}
    >
      {/* Leader indicator */}
      <div className="w-5 shrink-0">
        {isLeader && <Crown className="h-4 w-4 text-amber-400" />}
      </div>

      {/* Name and role */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('font-medium truncate', isLeader && 'text-amber-400')}>
            {candidate.name}
          </span>
          {isLeader && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-400 border-amber-500/30">
              Leader
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <CandidateRoleBadge role={candidate.role} />
          <span className="text-xs text-muted-foreground">
            Recognition: {candidate.recognition}
          </span>
        </div>
      </div>

      {/* Commitment level */}
      <div className="w-32 shrink-0">
        <div className="text-xs text-muted-foreground mb-1">Commitment</div>
        <CandidateStatBar
          label=""
          value={member.commitment_level}
          colorMode="neutral"
          className="h-1.5"
        />
      </div>

      {/* Defection indicator */}
      <div className="w-6 shrink-0">
        {member.will_defect_with_faction && (
          <Tooltip>
            <TooltipTrigger>
              <DoorOpen className="h-4 w-4 text-red-400" />
            </TooltipTrigger>
            <TooltipContent>Will defect with faction</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <Card className="animate-pulse">
      <CardHeader>
        <div className="h-6 bg-muted rounded w-1/3" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="h-20 bg-muted rounded" />
        <div className="h-40 bg-muted rounded" />
        <div className="h-32 bg-muted rounded" />
      </CardContent>
    </Card>
  )
}

function ErrorState({ error, onClose }: { error: Error; onClose?: () => void }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">Faction Details</CardTitle>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="p-3 rounded-full bg-red-500/10 mb-3">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="font-medium text-sm mb-1">Failed to load faction</h3>
          <p className="text-xs text-muted-foreground max-w-xs">
            {error.message || 'An error occurred while loading faction details'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export function FactionDetail({
  factionId,
  onClose,
  editable = false,
  currentTick = 0,
  className,
}: FactionDetailProps) {
  const { faction, isLoading, error } = useFactionWithMembers(factionId)
  const { party } = useParty(faction?.party_id)
  const { updateFactionNameAsync, isUpdating } = useUpdateFactionName()

  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState('')

  const startEditing = useCallback(() => {
    if (faction) {
      setEditedName(faction.name)
      setIsEditing(true)
    }
  }, [faction])

  const cancelEditing = useCallback(() => {
    setIsEditing(false)
    setEditedName('')
  }, [])

  const saveName = useCallback(async () => {
    if (!faction || !editedName.trim()) return

    try {
      await updateFactionNameAsync({ factionId: faction.id, name: editedName.trim() })
      setIsEditing(false)
    } catch {
      // Error is handled by the hook
    }
  }, [faction, editedName, updateFactionNameAsync])

  if (isLoading) {
    return <LoadingState />
  }

  if (error || !faction) {
    return <ErrorState error={error || new Error('Faction not found')} onClose={onClose} />
  }

  // Sort members by commitment level, leader first
  const sortedMembers = [...(faction.members || [])].sort((a, b) => {
    // Leader always first
    if (a.candidate.id === faction.leader_candidate_id) return -1
    if (b.candidate.id === faction.leader_candidate_id) return 1
    // Then by commitment level
    return b.commitment_level - a.commitment_level
  })

  const hasDemand = faction.demand_type && faction.demand_details

  return (
    <Card className={className}>
      {/* Header Section */}
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="h-8 text-lg font-semibold"
                maxLength={50}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName()
                  if (e.key === 'Escape') cancelEditing()
                }}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={saveName}
                disabled={isUpdating || !editedName.trim()}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={cancelEditing}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{faction.name}</CardTitle>
              {editable && (
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={startEditing}>
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <span>Formed on tick {faction.formed_tick}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {faction.member_count} members
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <FactionStatusBadge status={faction.status} size="lg" />
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Demand Alert (if active) */}
        {hasDemand && (
          <FactionDemandAlert
            faction={faction}
            demandDetails={faction.demand_details as unknown as FactionDemandDetails}
            currentTick={currentTick}
            variant="full"
          />
        )}

        {/* Satisfaction Section */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm">Satisfaction</h3>
          <FactionSatisfactionBar
            satisfaction={faction.satisfaction}
            size="lg"
            showThresholds={true}
            showLabel={true}
            animated={true}
          />
          <div className="grid grid-cols-4 gap-2 text-xs text-center mt-2">
            <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
              <div className="text-red-400 font-medium">0-19</div>
              <div className="text-muted-foreground">Threatening</div>
            </div>
            <div className="p-2 rounded bg-orange-500/10 border border-orange-500/20">
              <div className="text-orange-400 font-medium">20-39</div>
              <div className="text-muted-foreground">Demanding</div>
            </div>
            <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
              <div className="text-yellow-400 font-medium">40-69</div>
              <div className="text-muted-foreground">Concerned</div>
            </div>
            <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
              <div className="text-green-400 font-medium">70-100</div>
              <div className="text-muted-foreground">Content</div>
            </div>
          </div>
        </div>

        {/* Influence Section */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm">Influence</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${faction.influence}%` }}
                />
              </div>
            </div>
            <span className="font-medium text-lg tabular-nums w-16 text-right">
              {faction.influence}%
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Faction influence within the party, based on member count, roles, and recognition.
          </p>
        </div>

        {/* Ideology Section */}
        {faction.ideology_vector && party?.ideology_vector && (
          <div className="space-y-2">
            <h3 className="font-medium text-sm">Ideology Comparison</h3>
            <FactionIdeologyComparison
              factionIdeology={faction.ideology_vector}
              partyIdeology={party.ideology_vector}
              highlightDivergence={true}
              showLabels={true}
              compact={false}
            />
          </div>
        )}

        {/* Members Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm">Members</h3>
            <span className="text-xs text-muted-foreground">
              {sortedMembers.filter((m) => m.will_defect_with_faction).length} would defect
            </span>
          </div>

          {sortedMembers.length === 0 ? (
            <div className="text-center py-4 text-sm text-muted-foreground">
              No members in this faction
            </div>
          ) : (
            <div className="space-y-1">
              {sortedMembers.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isLeader={member.candidate.id === faction.leader_candidate_id}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
